/**
 * Created by neo on 23.11.20.
 */
import { makeAutoObservable } from 'mobx';
import { DayOfWeek } from '../DayOfWeek';
import {
  DurationBefore,
  durationOrder,
  NotificationInterval,
  createNotificationInterval,
} from './Schedule/NotificationInterval';
import { TimeOfDay } from '../TimeOfDay';
import { DefaultTimeRangeAny, ScheduleTimeRange, createScheduleTimeRange } from './ScheduleTimeRange';
import { TimeRange } from '../TimeRange';
import { TimesOfDay } from './TimesOfDay';

export type ScheduleEntries = {
  [key in DayOfWeek]?: ScheduleTimeRange[];
};

export type ProgramSchedule = {
  entries: ScheduleEntries;
  timezone: string;
  notificationIntervals: NotificationInterval[];
  timesOfDay: TimesOfDay;
  readonly earliestInterval: NotificationInterval | undefined;
  readonly latestInterval: NotificationInterval | undefined;
  readonly anyTimeRange: TimeRange | undefined;
};

export function createProgramSchedule(json?: Partial<ProgramSchedule>): ProgramSchedule {
  const entries: ScheduleEntries = json?.entries
    ? Array.from(Object.entries(json.entries))
        .map(([key, value]) => [key, (value ?? []).map((v: any) => createScheduleTimeRange(v))])
        .reduce((agg, [key, entries]) => Object.assign(agg, { [key as string]: entries }), {})
    : {};

  let timesOfDay: TimesOfDay = Object.entries(json?.timesOfDay ?? ({} as TimesOfDay))
    .map(([key, value]) => [key, createScheduleTimeRange(value as any)])
    .reduce((obj, [key, value]) => Object.assign(obj, { [key as TimeOfDay]: value }), {} as TimesOfDay);

  // build timesOfDay object if it was not given
  if (Array.from(Object.keys(timesOfDay)).length === 0 && Array.from(Object.values(entries)).length > 0) {
    timesOfDay = Array.from(Object.values(entries)).reduce(
      (agg, timeRanges) =>
        Object.assign(
          agg,
          (timeRanges ?? []).reduce((obj, timeRange) => ({ [timeRange.timeOfDay]: timeRange }), {}),
        ),
      {} as TimesOfDay,
    );
  } else if (Array.from(Object.keys(timesOfDay)).length === 0) {
    timesOfDay.any = createScheduleTimeRange(DefaultTimeRangeAny);
  }

  return makeAutoObservable({
    entries,
    timezone: json?.timezone ?? 'Europe/Zurich',
    notificationIntervals: (json?.notificationIntervals ?? []).map((n: any) => createNotificationInterval(n)) ?? [
      createNotificationInterval({ durationBefore: DurationBefore.HALF_HOUR_BEFORE }),
    ],
    timesOfDay,

    get earliestInterval(): NotificationInterval | undefined {
      return this.notificationIntervals
        .slice()
        .sort((a, b) => durationOrder.indexOf(b.durationBefore) - durationOrder.indexOf(a.durationBefore))[0];
    },

    get latestInterval(): NotificationInterval | undefined {
      return this.notificationIntervals
        .slice()
        .sort((a, b) => durationOrder.indexOf(a.durationBefore) - durationOrder.indexOf(b.durationBefore))[0];
    },

    get anyTimeRange(): TimeRange | undefined {
      // we need to do it like this otherwise mobx does not react
      const values =
        this.entries.MONDAY ||
        this.entries.TUESDAY ||
        this.entries.WEDNESDAY ||
        this.entries.THURSDAY ||
        this.entries.FRIDAY ||
        this.entries.SATURDAY ||
        this.entries.SUNDAY;

      return (values ?? [])[0];
    },
  });
}
